import type { CategoryRow, ProductTypeRow } from './ProductTypeTaxonomyGraph';

export const COL_WIDTH = 320;
export const ROW_HEIGHT = 168;
export const NODE_WIDTH = 264;

export const COMPACT_COL_WIDTH = 196;
export const COMPACT_ROW_HEIGHT = 72;
export const COMPACT_NODE_WIDTH = 168;

export type GraphLayoutMetrics = {
  colWidth: number;
  rowHeight: number;
  nodeWidth: number;
};

export function graphLayoutMetrics(compact: boolean): GraphLayoutMetrics {
  if (compact) {
    return {
      colWidth: COMPACT_COL_WIDTH,
      rowHeight: COMPACT_ROW_HEIGHT,
      nodeWidth: COMPACT_NODE_WIDTH,
    };
  }
  return { colWidth: COL_WIDTH, rowHeight: ROW_HEIGHT, nodeWidth: NODE_WIDTH };
}

export type NodePosition = { x: number; y: number; height: number };

function layoutCategorySubtree(
  catId: string,
  depth: number,
  yStart: number,
  childrenByParent: Map<string, CategoryRow[]>,
  positions: Map<string, NodePosition>,
  visited: Set<string>,
  metrics: GraphLayoutMetrics,
): number {
  if (visited.has(catId)) {
    // Defensive: avoid infinite loops if data has cycles.
    const existing = positions.get(catId);
    return existing?.height ?? 1;
  }
  visited.add(catId);

  const children = childrenByParent.get(catId) ?? [];
  if (children.length === 0) {
    positions.set(catId, {
      x: depth * metrics.colWidth,
      y: yStart * metrics.rowHeight,
      height: 1,
    });
    return 1;
  }

  let h = 0;
  for (const child of children) {
    h += layoutCategorySubtree(
      child.id,
      depth + 1,
      yStart + h,
      childrenByParent,
      positions,
      visited,
      metrics,
    );
  }
  const centerY = yStart + (h - 1) / 2;
  positions.set(catId, {
    x: depth * metrics.colWidth,
    y: centerY * metrics.rowHeight,
    height: h,
  });
  return h;
}

export function buildLayout(
  productTypes: ProductTypeRow[],
  categories: CategoryRow[],
  childrenByParent: Map<string, CategoryRow[]>,
  compact = false,
  vertical = false,
): {
  positions: Map<string, NodePosition>;
  /** Type code → position key (under `type-${code}`). */
  typeKey: (code: string) => string;
  metrics: GraphLayoutMetrics;
} {
  const metrics = graphLayoutMetrics(compact);
  const positions = new Map<string, NodePosition>();
  const visited = new Set<string>();

  const linkedRootIds = new Set<string>();
  for (const t of productTypes) for (const r of t.roots) linkedRootIds.add(r.categoryId);

  const typeKey = (code: string) => `type-${code}`;

  let yCursor = 0;

  // Sort types: sortOrder asc, then label asc — stable ordering for the column.
  const sortedTypes = [...productTypes].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.label.localeCompare(b.label, 'fr');
  });

  for (const type of sortedTypes) {
    // Each type's roots that exist in our category set.
    const rootIds = type.roots
      .map((r) => r.categoryId)
      .filter((id) => categories.some((c) => c.id === id));
    if (rootIds.length === 0) {
      positions.set(typeKey(type.code), {
        x: 0,
        y: yCursor * metrics.rowHeight,
        height: 1,
      });
      yCursor += 1;
      continue;
    }
    let h = 0;
    for (const rootId of rootIds) {
      h += layoutCategorySubtree(
        rootId,
        1,
        yCursor + h,
        childrenByParent,
        positions,
        visited,
        metrics,
      );
    }
    const centerY = yCursor + (h - 1) / 2;
    positions.set(typeKey(type.code), {
      x: 0,
      y: centerY * metrics.rowHeight,
      height: h,
    });
    yCursor += h;
  }

  // Orphan roots (not linked to any type) sit below the typed forest.
  const orphans = categories
    .filter((c) => c.parentId == null && !linkedRootIds.has(c.id))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  for (const orphan of orphans) {
    if (visited.has(orphan.id)) continue;
    const height = layoutCategorySubtree(
      orphan.id,
      1,
      yCursor,
      childrenByParent,
      positions,
      visited,
      metrics,
    );
    yCursor += height;
  }

  if (vertical) {
    const depthPitch = compact ? 108 : metrics.rowHeight;
    for (const [id, p] of positions) {
      const depth = metrics.colWidth === 0 ? 0 : p.x / metrics.colWidth;
      const slot = metrics.rowHeight === 0 ? 0 : p.y / metrics.rowHeight;
      positions.set(id, {
        x: slot * metrics.colWidth,
        y: depth * depthPitch,
        height: p.height,
      });
    }
  }

  return { positions, typeKey, metrics };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Emphasis — categories owned by selected type are "on-path"; categories
 * owned by other types are "muted"; orphans are "orphan"; the selected type
 * itself is "selected".
 * ────────────────────────────────────────────────────────────────────────── */

export function computeEmphasis(
  productTypes: ProductTypeRow[],
  childrenByParent: Map<string, CategoryRow[]>,
  selectedTypeCode: string,
): {
  ownersByCategory: Map<string, Set<string>>;
  onPathCategories: Set<string>;
} {
  const ownersByCategory = new Map<string, Set<string>>();

  for (const t of productTypes) {
    const seen = new Set<string>();
    const queue = t.roots.map((r) => r.categoryId);
    while (queue.length) {
      const cid = queue.shift()!;
      if (seen.has(cid)) continue;
      seen.add(cid);
      const set = ownersByCategory.get(cid) ?? new Set<string>();
      set.add(t.code);
      ownersByCategory.set(cid, set);
      for (const child of childrenByParent.get(cid) ?? []) queue.push(child.id);
    }
  }

  const onPathCategories = new Set<string>();
  for (const [cid, owners] of ownersByCategory) {
    if (owners.has(selectedTypeCode)) onPathCategories.add(cid);
  }

  return { ownersByCategory, onPathCategories };
}
